import { useTranslation } from "react-i18next"
import { useLocation } from "react-router"

import { useActiveOrganizationId, useAuthentication } from "@/contexts"
import { WYNN_ID } from "@/models/organization"
import { useFlags } from "@/services/feature"
import { useUserAccess } from "@/services/user"
import { useUrl } from "@/utils/url"

import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined"
import HomeWorkIcon from "@mui/icons-material/HomeWork"
import InsightsIcon from "@mui/icons-material/Insights"
import ScienceIcon from "@mui/icons-material/Science"
import SettingsIcon from "@mui/icons-material/Settings"
import { List } from "@mui/material"

import { NavItemButton } from "../nav-item-button/nav-item-button"

interface MainNavListProps {
  variant?: "default" | "compact"
}

export const MainNavList = ({ variant = "default" }: MainNavListProps) => {
  const { t } = useTranslation()
  const flags = useFlags()

  const location = useLocation()
  const {
    adminPath,
    dataPath,
    financialPath,
    labsPath,
    reportingPath,
    sitesPath,
  } = useUrl()
  const {
    isAdminUser,
    isOrgCredentialsAdminUser,
    isNZeroOSUser,
    isOrgAdminUser,
  } = useAuthentication()
  const { activeOrganizationId } = useActiveOrganizationId()
  const { isLimitedAccessUser } = useUserAccess()

  const isActiveOrganizationWynn = activeOrganizationId === WYNN_ID

  const isCompact = variant === "compact"

  return (
    <List sx={{ flex: 1, overflowY: "auto", pt: 0 }}>
      {isNZeroOSUser ? (
        <NavItemButton
          dataE2e="analyze-link"
          from={location.pathname}
          icon={<InsightsIcon />}
          id="analyze-button"
          isOpen={!isCompact}
          title={t("moduleTitle.analyze", { ns: "common" })}
          to={reportingPath}
        />
      ) : null}
      {isNZeroOSUser &&
      flags.financial_overview &&
      !isActiveOrganizationWynn ? (
        <NavItemButton
          dataE2e="organization-link"
          from={location.pathname}
          icon={<AccountBalanceIcon />}
          id="organization-button"
          isOpen={!isCompact}
          title={t("moduleTitle.financial", { ns: "common" })}
          to={financialPath}
        />
      ) : null}
      {isNZeroOSUser && !isActiveOrganizationWynn ? (
        <NavItemButton
          dataE2e="sites-link"
          from={location.pathname}
          icon={<HomeWorkIcon />}
          id="sites-link"
          isOpen={!isCompact}
          title={t("moduleTitle.sites", { ns: "common" })}
          to={sitesPath}
        />
      ) : null}
      {isAdminUser ||
      isOrgAdminUser ||
      (flags.data_manager &&
        !isOrgCredentialsAdminUser &&
        !isLimitedAccessUser &&
        !isActiveOrganizationWynn) ? (
        <NavItemButton
          dataE2e="data-manager-link"
          from={location.pathname}
          icon={<DatasetOutlinedIcon />}
          id="data-manager-button"
          isOpen={!isCompact}
          title={t("moduleTitle.data", { ns: "common" })}
          to={dataPath}
        />
      ) : null}
      {isAdminUser || flags.labs ? (
        <NavItemButton
          dataE2e="labs-link"
          from={location.pathname}
          icon={<ScienceIcon />}
          id="labs-button"
          isOpen={!isCompact}
          title={t("moduleTitle.labs", { ns: "common" })}
          to={labsPath}
        />
      ) : null}
      {/* Administration module */}
      {(isAdminUser || isOrgAdminUser) && flags.admin ? (
        <NavItemButton
          dataE2e="administration-link"
          from={location.pathname}
          icon={<SettingsIcon />}
          id="administration-button"
          isOpen={!isCompact}
          title={t("moduleTitle.administration", { ns: "common" })}
          to={adminPath}
        />
      ) : null}
      {!isAdminUser &&
      !isOrgAdminUser &&
      isOrgCredentialsAdminUser &&
      flags.admin &&
      flags.admin_credentials ? (
        <NavItemButton
          dataE2e="administration-link"
          from={location.pathname}
          icon={<SettingsIcon />}
          id="administration-button"
          isOpen={!isCompact}
          title={t("moduleTitle.administration", { ns: "common" })}
          to={adminPath}
        />
      ) : null}
    </List>
  )
}
